import React, { createContext, useCallback, useContext, useEffect, useReducer, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import {
  createDestinationReviewPendingAction,
  createFavoritePendingAction,
  createMediaContributionPendingAction,
  createTrailRatingPendingAction,
  pendingAuthReducer,
  resumePendingAuthAction,
} from "@/lib/contextualAuth";
import { destinationReviewReturnTo } from "@/lib/destinationReviews";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const location = useLocation();
  const [user, setUser] = useState(null); // null = checking, object = logged in, false = anon
  const [favIds, setFavIds] = useState([]); // [{item_id, item_type}]
  const [pendingAction, dispatchPendingAction] = useReducer(pendingAuthReducer, null);

  const loadFavorites = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites");
      const ids = data.ids || [];
      setFavIds(ids);
      return ids;
    } catch {
      setFavIds([]);
      return [];
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
        await loadFavorites();
      } catch {
        setUser(false);
      }
    })();
  }, [loadFavorites]);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data.user);
    return data.user;
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    await loadFavorites();
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setUser(data.user);
    await loadFavorites();
    return data;
  };

  const googleLogin = async (credential) => {
    const { data } = await api.post("/auth/google", { credential });
    setUser(data.user);
    await loadFavorites();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setFavIds([]);
    dispatchPendingAction({ type: "LOGOUT" });
  };

  const isFavorite = (itemId, itemType) =>
    favIds.some((f) => f.item_id === itemId && f.item_type === itemType);

  const addFavorite = useCallback(async (itemId, itemType) => {
    await api.post("/favorites", { item_id: itemId, item_type: itemType });
    setFavIds((prev) => (
      prev.some((f) => f.item_id === itemId && f.item_type === itemType)
        ? prev
        : [...prev, { item_id: itemId, item_type: itemType }]
    ));
  }, []);

  const toggleFavorite = async (itemId, itemType) => {
    if (!user) {
      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      dispatchPendingAction({
        type: "REQUEST",
        action: createFavoritePendingAction({ itemId, itemType, returnTo }),
      });
      return { needAuth: true };
    }

    if (isFavorite(itemId, itemType)) {
      await api.delete("/favorites", { params: { item_id: itemId, item_type: itemType } });
      setFavIds((prev) => prev.filter((f) => !(f.item_id === itemId && f.item_type === itemType)));
      return { removed: true };
    }

    await addFavorite(itemId, itemType);
    return { added: true };
  };

  const requestDestinationReview = useCallback((destinationId) => {
    if (user) return { needAuth: false };
    dispatchPendingAction({
      type: "REQUEST",
      action: createDestinationReviewPendingAction({
        destinationId,
        returnTo: destinationReviewReturnTo(destinationId),
      }),
    });
    return { needAuth: true };
  }, [user]);

  const setTrailRating = useCallback(async (trailId, rating) => {
    return api.post(`/trails/${trailId}/rating`, { rating });
  }, []);

  const requestTrailRating = useCallback((trailId, rating) => {
    if (user) return { needAuth: false };
    dispatchPendingAction({
      type: "REQUEST",
      action: createTrailRatingPendingAction({
        trailId,
        rating,
        returnTo: `${location.pathname}${location.search}${location.hash}`,
      }),
    });
    return { needAuth: true };
  }, [location.hash, location.pathname, location.search, user]);

  const requestMediaContribution = useCallback(({ destinationId, trailId, mode }) => {
    if (user) return { needAuth: false };
    const query = new URLSearchParams(location.search);
    query.set("media", mode);
    dispatchPendingAction({
      type: "REQUEST",
      action: createMediaContributionPendingAction({
        destinationId,
        trailId,
        mode,
        returnTo: `${location.pathname}?${query.toString()}#community-media`,
      }),
    });
    return { needAuth: true };
  }, [location.pathname, location.search, user]);

  const cancelPendingAction = useCallback(() => {
    dispatchPendingAction({ type: "CANCEL" });
  }, []);

  const authenticationFailed = useCallback(() => {
    dispatchPendingAction({ type: "AUTH_FAILED" });
  }, []);

  const resumePendingAction = useCallback(async () => {
    const action = pendingAction;
    if (!action) return { resumed: false, returnTo: "/" };

    // Consume before replay so retries or rerenders can never execute it twice.
    dispatchPendingAction({ type: "RESUME_STARTED" });
    return resumePendingAuthAction(action, { addFavorite, setTrailRating });
  }, [addFavorite, pendingAction, setTrailRating]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        googleLogin,
        refreshUser,
        logout,
        isFavorite,
        toggleFavorite,
        favIds,
        loadFavorites,
        requestDestinationReview,
        requestTrailRating,
        requestMediaContribution,
        pendingAction,
        cancelPendingAction,
        authenticationFailed,
        resumePendingAction,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
